import React from 'react';
import { InlineNotification } from '@carbon/react';
import {
  TractNRIFormulaData,
  TractNRICard,
  TractWICard,
} from 'components/tract-nri-card';
import InformationIconTrigger from 'components/information-icon-trigger';
import {
  buildUnitNRICalculation,
  AcreageUnitInterestFormula,
  LateralUnitInterestFormula,
  getUnitNRIUnroundedFormula,
} from './calculation-builder';
import { CopyButton } from 'components/copy-button/copy-button';
import style from './unit-interest-card.module.scss';

export interface UnitInterestCardContentProps {
  unitInterestValue: number | null;
  activeAllocationMethod: 'acreage' | 'lateral_length';
  lateralUnitInterestFormula: LateralUnitInterestFormula | null;
  acreageUnitInterestFormula: AcreageUnitInterestFormula | null;
  hasAllocationManager: boolean;
  calculationMethod: 'nra' | 'nma';
  tractNRIFormula: TractNRIFormulaData | null;
  participationFactorOverridden?: boolean;
  title: string;
  /** Which acreage figures drive the third factor. Defaults to today's GIS view. */
  acreageBasis?: 'gis' | 'legal';
  /**
   * Rendered under the formula. The CRM wizard passes its editable overlap panel
   * here; every other surface omits it and is unaffected.
   */
  acreageBasisPanel?: React.ReactNode;
}

const FormulaBox: React.FC<{
  topLabel: string;
  bottomLabel: string;
  className?: string;
}> = ({ topLabel, bottomLabel, className = '' }) => (
  <div className={`${style.formulaBox} ${className}`}>
    <div className={style.topLabel}>{topLabel}</div>
    <div className={style.underline}>
      <div className={style.bottomLabel}>{bottomLabel}</div>
    </div>
  </div>
);

const MultiplicationSymbol: React.FC = () => (
  <div className={style.multiplicationSymbol}>*</div>
);

const CardHeader: React.FC<{
  title: string;
  allocationMethodHeader: string;
}> = ({ title, allocationMethodHeader }) => (
  <h4 className={style.cardTitle}>
    <span className={style.titleBold}>{title}</span>{' '}
    <span className={style.titleNormal}>{`(${allocationMethodHeader})`}</span>
  </h4>
);

const TractNRISection: React.FC<{
  tractNRIFormula: TractNRIFormulaData | null;
  calculationMethod: 'nra' | 'nma';
  tractNri: number;
}> = ({ tractNRIFormula, calculationMethod, tractNri }) => {
  const tractNriRenderTrigger = (active: boolean, toggle: () => void) => (
    <span className={style.tractNriTriggerWrapper}>
      <InformationIconTrigger active={active} onClick={toggle} />
    </span>
  );

  const labelText = 'Tract NRI';

  return tractNRIFormula ? (
    <span className={style.tractNriContainer}>
      <span className={style.tractNriText}>{labelText}</span>
      <TractNRICard
        calculationMethod={calculationMethod}
        tractNriFormula={tractNRIFormula}
        tractNri={tractNri}
        renderTrigger={tractNriRenderTrigger}
        instrumentType={tractNRIFormula.instrumentType}
      />
    </span>
  ) : (
    <span className={style.tractNriContainer}>
      <span className={style.tractNriText}>{labelText}</span>
    </span>
  );
};

const TractWISection: React.FC<{
  tractNRIFormula: TractNRIFormulaData | null;
  tractWi: number;
}> = ({ tractNRIFormula, tractWi }) => {
  const tractWiRenderTrigger = (active: boolean, toggle: () => void) => (
    <span className={style.tractNriTriggerWrapper}>
      <InformationIconTrigger active={active} onClick={toggle} />
    </span>
  );

  const labelText = 'Tract WI';
  const isOGL = tractNRIFormula?.instrumentType?.toUpperCase() === 'OGL';

  return tractNRIFormula ? (
    <span className={style.tractNriContainer}>
      <span className={style.tractNriText}>{labelText}</span>
      {isOGL && (
        <TractWICard
          tractWiFormula={tractNRIFormula}
          tractWi={tractWi}
          renderTrigger={tractWiRenderTrigger}
        />
      )}
    </span>
  ) : (
    <span className={style.tractNriContainer}>
      <span className={style.tractNriText}>{labelText}</span>
    </span>
  );
};

const FormulaExplanation: React.FC<{
  hasAllocationManager: boolean;
  tractNRIFormula: TractNRIFormulaData | null;
  calculationMethod: 'nra' | 'nma';
  formulaMetadata:
    AcreageUnitInterestFormula | LateralUnitInterestFormula | null;
  participationFactorOverridden?: boolean;
  isWI?: boolean;
  /** Which acreage figures drive the third factor. Defaults to today's GIS view. */
  acreageBasis?: 'gis' | 'legal';
}> = ({
  hasAllocationManager,
  tractNRIFormula,
  calculationMethod,
  formulaMetadata,
  acreageBasis = 'gis',
  participationFactorOverridden,
  isWI,
}) => (
  <div className={style.formulaExplanation}>
    {!isWI && (
      <TractNRISection
        tractNRIFormula={tractNRIFormula}
        calculationMethod={calculationMethod}
        tractNri={formulaMetadata?.tractNri ?? 0}
      />
    )}
    {isWI && (
      <TractWISection
        tractNRIFormula={tractNRIFormula}
        tractWi={formulaMetadata?.wi ?? 0}
      />
    )}

    <MultiplicationSymbol />

    <FormulaBox
      topLabel={
        hasAllocationManager
          ? 'UAT value (ac or ft)'
          : 'Unit Tract (Gross Acres)'
      }
      bottomLabel={
        hasAllocationManager
          ? 'Unit Tract or Total Lateral Length'
          : 'Unit Tract (Gross Acres)'
      }
    />

    <MultiplicationSymbol />

    <FormulaBox
      // Under legal acreage the third factor is the document's gross acres,
      // scaled to the portion inside the UAT, over the UAT's plat acreage.
      topLabel={
        acreageBasis === 'legal'
          ? 'Tract Gross x Overlap (Participation Tract)'
          : 'Participation Tract'
      }
      className={participationFactorOverridden ? style.highlight : ''}
      bottomLabel={
        acreageBasis === 'legal'
          ? 'UAT value (plat ac)'
          : hasAllocationManager
            ? 'UAT value (GIS ac)'
            : 'Unit Tract (Gross Acres)'
      }
    />

    <div className={style.equalsContainer}>
      <div className={style.equalsSymbol}>=</div>
      <div className={style.unitInterest}>{isWI ? 'Unit WI' : 'Unit NRI'}</div>
    </div>
  </div>
);

const getInterestAllocationMethodHeader = (
  allocationMethod: 'acreage' | 'lateral_length'
): string => {
  if (allocationMethod === 'lateral_length') {
    return 'Lateral Length Allocation Method';
  }
  return 'Acreage Allocation Method';
};

export const UnitInterestCardContent: React.FC<
  UnitInterestCardContentProps
> = ({
  unitInterestValue,
  activeAllocationMethod,
  lateralUnitInterestFormula,
  acreageUnitInterestFormula,
  hasAllocationManager,
  calculationMethod,
  tractNRIFormula,
  participationFactorOverridden,
  title = 'Unit NRI Calculation',
  acreageBasis = 'gis',
  acreageBasisPanel,
}) => {
  const isWI = title.toLowerCase().includes('wi');
  const allocationMethodHeader = getInterestAllocationMethodHeader(
    activeAllocationMethod
  );
  const numericValue =
    typeof unitInterestValue === 'string'
      ? parseFloat(unitInterestValue)
      : unitInterestValue;
  const showWarning = !numericValue;
  const formulaMetadata =
    activeAllocationMethod === 'lateral_length'
      ? lateralUnitInterestFormula
      : acreageUnitInterestFormula;

  return (
    <div className={style.unitInterestCalculationCard}>
      <CardHeader
        title={title}
        allocationMethodHeader={allocationMethodHeader}
      />
      {showWarning && (
        <div className={style.warningContainer}>
          <InlineNotification
            kind="warning"
            title="Warning"
            subtitle={
              isWI
                ? 'Missing Working Interest Value'
                : 'Missing Royalty Rate in Tract NRI Calculation'
            }
            hideCloseButton
          />
        </div>
      )}
      {acreageBasisPanel}

      <div className={style.calculationFormula}>
        <div className={style.formulaWithCopy}>
          <div className={style.formulaText}>
            {formulaMetadata &&
              buildUnitNRICalculation(
                activeAllocationMethod,
                formulaMetadata,
                numericValue ?? 0,
                isWI
              )}
          </div>
          {formulaMetadata && (
            <div className={style.copyButtonWrapper}>
              <CopyButton
                className={style.unitInterestCopyButton}
                node={getUnitNRIUnroundedFormula(
                  activeAllocationMethod,
                  formulaMetadata,
                  numericValue ?? 0
                )}
              />
            </div>
          )}
        </div>
      </div>

      <FormulaExplanation
        acreageBasis={acreageBasis}
        hasAllocationManager={hasAllocationManager}
        tractNRIFormula={tractNRIFormula}
        calculationMethod={calculationMethod}
        formulaMetadata={formulaMetadata}
        isWI={isWI}
        participationFactorOverridden={participationFactorOverridden}
      />
    </div>
  );
};
